import React, {useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {MoreHorizontal} from 'lucide-react';
import Button from './Button';

// Contextual "⋯" menu, same frosted-glass panel as AppleSelect. One look for
// every row / header action menu: teal highlight, red for destructive items.
// Portalled to <body> so it is never clipped by a table's overflow.
//
// items: [{label, icon, onClick, danger, disabled, hidden} | {divider: true} | {header: 'Titre'}]
// trigger: optional props for the trigger Button (variant, size, children…)

const GAP = 6;
const WIDTH = 224;

const ActionMenu = ({items, label = 'Actions', align = 'right', trigger = {}}) => {
    const visible = items.filter(i => i && !i.hidden);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [pos, setPos] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const menuId = useId();

    const actionable = visible.map((it, i) => (!it.divider && !it.header && !it.disabled ? i : -1)).filter(i => i >= 0);

    const place = useCallback(() => {
        const r = buttonRef.current?.getBoundingClientRect();
        if (!r) return;
        const height = menuRef.current?.offsetHeight || visible.length * 36 + 12;
        const flip = window.innerHeight - r.bottom < height + GAP + 8 && r.top > height + GAP;
        const left = align === 'right' ? r.right - WIDTH : r.left;
        setPos({
            left: Math.max(8, Math.min(left, window.innerWidth - WIDTH - 8)),
            ...(flip ? {bottom: window.innerHeight - r.top + GAP} : {top: r.bottom + GAP}),
            origin: `${flip ? 'bottom' : 'top'} ${align}`,
        });
    }, [align, visible.length]);

    const close = (refocus = true) => {
        setOpen(false);
        if (refocus) buttonRef.current?.focus();
    };

    const run = (item) => {
        if (!item || item.disabled || item.divider || item.header) return;
        close();
        item.onClick?.();
    };

    useLayoutEffect(() => {
        if (!open) return;
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, place]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) close(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    });

    const step = (dir) => {
        if (!actionable.length) return;
        const at = actionable.indexOf(active);
        setActive(actionable[(at + dir + actionable.length) % actionable.length] ?? actionable[0]);
    };

    const onKeyDown = (e) => {
        if (!open) {
            if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                setActive(actionable[0] ?? -1);
                setOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); run(visible[active]); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
        else if (e.key === 'Tab') close(false);
    };

    const {children: triggerLabel, ...triggerProps} = trigger;

    return (
        <>
            <Button
                ref={buttonRef}
                variant="ghost"
                size="sm"
                icon={triggerLabel ? undefined : MoreHorizontal}
                title={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                {...triggerProps}
                onClick={(e) => {
                    e.stopPropagation();
                    setActive(-1);
                    setOpen(o => !o);
                }}
                onKeyDown={onKeyDown}
                className={`${open ? '!bg-primary/10 !text-primary' : ''} ${triggerProps.className || ''}`}
            >
                {triggerLabel}
            </Button>

            {open && createPortal(
                <div
                    ref={menuRef}
                    id={menuId}
                    role="menu"
                    aria-label={label}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed', width: WIDTH, left: pos?.left ?? -9999, top: pos?.top, bottom: pos?.bottom,
                        transformOrigin: pos?.origin, visibility: pos ? 'visible' : 'hidden',
                    }}
                    className="apple-select-menu z-[10000] p-1.5 rounded-2xl
                        bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl backdrop-saturate-150
                        border border-white/60 dark:border-white/10
                        shadow-[0_12px_40px_-8px_rgba(0,0,0,0.25),0_0_0_0.5px_rgba(0,0,0,0.08)]
                        dark:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.6),0_0_0_0.5px_rgba(255,255,255,0.06)]"
                >
                    {visible.map((item, i) => {
                        if (item.divider) return <div key={`d${i}`} role="separator" className="my-1 mx-2 h-px bg-gray-200/80 dark:bg-white/10"/>;
                        if (item.header) {
                            return (
                                <div key={`h${i}`} className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    {item.header}
                                </div>
                            );
                        }
                        const Icon = item.icon;
                        const isActive = i === active;
                        return (
                            <button
                                key={item.label}
                                type="button"
                                role="menuitem"
                                disabled={item.disabled}
                                onMouseEnter={() => !item.disabled && setActive(i)}
                                onMouseLeave={() => setActive(-1)}
                                onClick={() => run(item)}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left transition-colors duration-75 disabled:opacity-40 disabled:cursor-not-allowed
                                    ${isActive
                                        ? item.danger ? 'bg-red-500 text-white' : 'bg-primary text-white'
                                        : item.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}
                            >
                                {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? '' : item.danger ? '' : 'text-gray-500 dark:text-gray-400'}`}/>}
                                <span className="truncate">{item.label}</span>
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
};

export default ActionMenu;
